import { Suspense as DefaultSuspense, useReducer } from 'react'
import clsx from 'clsx'
import { Trans, useLingui } from '@lingui/react/macro'
import { VisuallyHidden } from '@radix-ui/react-visually-hidden'
import * as Dialog from '@radix-ui/react-dialog'
import * as Sentry from '@sentry/react'
import { ServerError } from '@apollo/client/errors'

import Loading from './Loading'

function useExtractError(error: unknown): string {
  const { t } = useLingui()

  if (ServerError.is(error)) {
    try {
      const { error: msg } = JSON.parse(error.bodyText)

      return msg
    } catch {
      // Let it fall threw
    }
  }

  if (!error) return t`Unknown error`

  return error.toString()
}

function Callback({
  error,
  isFullPage = false,
  reset,
  resetError
}: {
  error: unknown
  isFullPage?: boolean
  reset: () => void
  resetError: () => void
}) {
  const errorMsg = useExtractError(error)

  return (
    <div
      className={clsx(
        'flex items-center justify-center',
        isFullPage && 'size-full'
      )}
    >
      <div
        role="alert"
        className="mb-12 flex w-full flex-col gap-2 p-4 text-center"
      >
        <p className="text-nav-foreground">
          <Trans>Something went wrong</Trans>:
        </p>
        <p className="text-nav-foreground">{errorMsg}</p>
        <div className="flex flex-row justify-center gap-5 pt-2">
          {isFullPage && (
            <button
              className="border-none text-primary"
              onClick={() => {
                location.href = 'https://guestgoodie.com'
              }}
            >
              <Trans>To Homepage</Trans>
            </button>
          )}
          <button
            className="border-none text-primary"
            onClick={() => {
              resetError()
              // Compleley remount all children, so that it basically tries again.
              reset()
            }}
          >
            <Trans>Reset</Trans>
          </button>
        </div>
      </div>
    </div>
  )
}

export const Suspense: React.FC<
  React.PropsWithChildren<{
    isFullPage?: boolean
    scopeTags?: Record<string, string>
    isDialog?: boolean
  }>
> = function ({ children, isDialog, isFullPage, scopeTags }) {
  const [resetKey, reset] = useReducer(x => x + 1, 0)

  function LoadingFallback() {
    return (
      <div
        className={clsx(
          'flex items-center justify-center py-5',
          isFullPage && 'size-full'
        )}
      >
        {isDialog && (
          <>
            <VisuallyHidden asChild>
              <Dialog.Title>
                <Trans>Loading dialog</Trans>
              </Dialog.Title>
            </VisuallyHidden>
            <VisuallyHidden asChild>
              <Dialog.Description>
                <Trans>Currently loading the dialog content</Trans>
              </Dialog.Description>
            </VisuallyHidden>
          </>
        )}
        <Loading />
      </div>
    )
  }

  return (
    <Sentry.ErrorBoundary
      beforeCapture={scope => {
        if (!scopeTags) return

        Object.entries(scopeTags).forEach(([key, value]) =>
          scope.setTag(key, value)
        )
      }}
      fallback={({ error, resetError }) => {
        return (
          <Callback
            error={error}
            isFullPage={isFullPage}
            reset={reset}
            resetError={resetError}
          />
        )
      }}
      showDialog
    >
      <DefaultSuspense key={resetKey} fallback={<LoadingFallback />}>
        {children}
      </DefaultSuspense>
    </Sentry.ErrorBoundary>
  )
}
